import '@Globals/Sentry'

import { ApolloProvider } from '@apollo/client'
import fromPairs from 'lodash/fromPairs'
import includes from 'lodash/includes'
import noop from 'lodash/noop'
import App, { AppContext, AppInitialProps, AppProps } from 'next/app'
import getConfig from 'next/config'
import Head from 'next/head'
import { appWithTranslation } from 'next-i18next'
import React, { useMemo } from 'react'
import { PopupProvider } from 'react-popup-manager'
import { ThemeProvider } from 'styled-components'

import { Event } from '@Events/Base'
import { FireQueuedEvents } from '@Events/Manager'
import { Category } from '@Generated/graphql'
import { initializeApollo } from '@Globals/Apollo'
import emptyStore from '@Globals/emptyStore'
import { AnalyticsInitialize, AnalyticsSetup } from '@Globals/Google/Analytics'
import { DataLayer, DataLayerInit } from '@Globals/Google/DataLayer'
import { GTagScript } from '@Globals/Google/GTag'
import {
  AvailableLocale,
  Bar,
  CMSData,
  InfoData,
  MetadataProvider,
  PaymentMethod,
  Store,
} from '@Globals/Metadata'
import { ScrollPositionProvider } from '@Globals/ScrollPosition'
import { SearchProvider } from '@Globals/Search/Context'
import SiftInit from '@Globals/Sift'
import { SplitTests } from '@Globals/SplitTests'
import { SplitTestsProvider } from '@Globals/SplitTests/Context'
import { Group } from '@Globals/SplitTests/Group'
import StyleReset from '@Globals/Style/StyleReset'
import theme from '@Globals/Style/theme'
import { TimeUsedOnPage } from '@Globals/TimeUsedOnPage'
import { UserProvider } from '@Globals/User'
import { UserCentricsScript } from '@Globals/Usercentrics/script'

interface MyAppProps extends AppProps<any> {
  activateSplitTest: (test: string, group: Group) => void
  addSurrogateKey: (key: string) => void
  availableLocales: AvailableLocale[]
  bars: Bar[]
  categories: Category[]
  cmsData: CMSData
  infoData: InfoData
  isLocal: boolean
  paymentMethods: PaymentMethod[]
  splitTests: SplitTests
  store: Store
}

interface MyAppInitialProps extends AppInitialProps {
  activateSplitTest: (test: string, group: Group) => void
  addSurrogateKey: (key: string) => void
  availableLocales: AvailableLocale[]
  bars: Bar[]
  categories: Category[]
  cmsData: CMSData
  infoData: InfoData
  isLocal: boolean
  paymentMethods: PaymentMethod[]
  splitTests: SplitTests
  store: Store
}

const MyApp = (
  {
    Component,
    activateSplitTest,
    addSurrogateKey,
    availableLocales,
    bars,
    categories,
    cmsData,
    infoData,
    isLocal,
    pageProps,
    paymentMethods,
    splitTests,
    store,
  }: MyAppProps,
) => {
  const { publicRuntimeConfig: { mParticleAPIKey } } = getConfig()

  const selectFont = () => {
    const extendedLatinCountries = ['PL', 'CZ', 'SK', 'HU', 'RO']

    const countryCode = store?.config?.countryCode
    if (includes(extendedLatinCountries, countryCode)) {
      return 'gotham-latin-extended'
    }
    if (countryCode === 'GR') {
      return 'gotham-greek'
    }
    if (countryCode === 'BG') {
      return 'gotham-cyrillic'
    }
    return 'gotham-basic'
  }

  Event.setSplitTests(splitTests)
  Event.setStore(store)

  const locale = store?.config?.localeCode
  const previewEnvironment = pageProps.previewEnvironment || undefined
  const apolloClient = useMemo(
    () => initializeApollo(pageProps.initialApolloState || undefined, { locale, previewEnvironment }),
    [pageProps.initialApolloState, locale, previewEnvironment],
  )

  return (
    <SplitTestsProvider
      value={{
        activateSplitTest: activateSplitTest || noop,
        splitTests,
      }}
    >
      <ThemeProvider theme={theme}>
        <StyleReset />
        <Head>
          <meta
            content="width=device-width,initial-scale=1,maximum-scale=1,user-scalable=0"
            name="viewport"
          />
          <link
            href={`/static/fonts/${selectFont()}.css`}
            rel="stylesheet"
          />
          <DataLayerInit />
        </Head>
        <DataLayer
          input={{
            HotjarSiteID: 70023,
            country: store?.config?.countryCode,
            currencyRates: fromPairs(store?.config?.adwordsCurrencyRatesMap),
            localCurrencyKey: store?.config?.currencyCode,
            mParticleAPIKey,
            mParticleDisabled: false,
            storeId: store?.id,
          }}
        />
        <GTagScript measurementID={`AW-${store?.config?.adwordsTrackingID}`} />
        <AnalyticsInitialize />
        <UserCentricsScript enabled={store?.config?.usercentricsEnabled} />
        <MetadataProvider
          addSurrogateKey={addSurrogateKey || noop}
          alternateLinks={pageProps?.alternateLinks || []}
          availableLocales={availableLocales}
          bars={bars}
          categories={categories}
          cmsData={cmsData}
          infoData={infoData}
          isLocal={isLocal}
          page={pageProps?.page}
          paymentMethods={paymentMethods}
          store={store}
        >
          <AnalyticsSetup />
          <ApolloProvider client={apolloClient}>
            <UserProvider>
              <PopupProvider>
                <SearchProvider>
                  <ScrollPositionProvider>
                    <Component {...pageProps} />
                  </ScrollPositionProvider>
                  <TimeUsedOnPage />
                </SearchProvider>
              </PopupProvider>
            </UserProvider>
          </ApolloProvider>
          <FireQueuedEvents />
        </MetadataProvider>
      </ThemeProvider>
      {(store.id === '27' || store.id === '36') && <SiftInit />}
    </SplitTestsProvider>
  )
}

MyApp.getInitialProps = async (appContext: AppContext): Promise<MyAppInitialProps> => {
  const appProps = await App.getInitialProps(appContext)

  const addSurrogateKey = appContext?.ctx?.res?.thAddSurrogateKey || noop
  const bars = appContext?.ctx?.req?.thBars || []
  const availableLocales = appContext?.ctx?.req?.thAvailableLocales || []
  const categories = appContext?.ctx?.req?.thCategories || []
  const cmsData: CMSData = appContext?.ctx?.req?.thCMSData || {
    latestArticle: null,
    latestCollections: [],
    urls: {
      csr: '',
      aboutUs: '',
      cookiePolicy: '',
      privacyPolicy: '',
      terms: '',
    },
  }
  const infoData = appContext?.ctx?.req?.thInfoData || {
    extraURLs: [],
    urls: {
      delivery: '',
      paymentAndSecurity: '',
      returns: '',
    },
  }
  const isLocal = appContext?.ctx?.req?.thIsLocal || false
  const paymentMethods = appContext?.ctx?.req?.thPaymentMethods || []
  const splitTests = appContext?.ctx?.req?.thSplitTests || {}
  const store = appContext?.ctx?.req?.thStore || { ...emptyStore }

  return {
    ...appProps,
    activateSplitTest: (test: string, group: Group) => {
      console.info(`[split-test] activating ${test} ${group}`)

      const res = appContext?.ctx?.res
      if (!res) {
        return
      }

      const header = splitTests[test]?.header
      if (!header) {
        console.info(`[split-test] no header defined for ${test} ${group}`)
        return
      }

      splitTests[test].active = true

      res.thAddVary(header)
    },
    addSurrogateKey,
    availableLocales,
    bars,
    categories,
    cmsData,
    infoData,
    isLocal,
    pageProps: (appProps.pageProps || {}),
    paymentMethods,
    splitTests,
    store,
  }
}

export default appWithTranslation(MyApp)
